// modules/social-accounts/AccountForm.tsx
// Панель: input Account Name → отримати OAuth URL → показати + скопіювати

import React, { useState, useId } from 'react';
import { api, ApiClientError } from '../../core/apiClient.ts';
import type { OAuthUrlResponse } from '../../core/types.ts';

interface AccountFormProps {
  /** Callback після успішного отримання URL (для показу нотифікацій) */
  onUrlGenerated?: (url: string) => void;
}

export function AccountForm({ onUrlGenerated }: AccountFormProps) {
  const inputId = useId();

  const [accountName, setAccountName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthResult, setOauthResult] = useState<OAuthUrlResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountName.trim()) {
      setError('Введіть ім\'я акаунту');
      return;
    }

    setLoading(true);
    setError(null);
    setOauthResult(null);
    setCopied(false);

    try {
      const result = await api.get<OAuthUrlResponse>(
        `/api/accounts/oauth-url?account_name=${encodeURIComponent(accountName.trim())}`
      );
      setOauthResult(result);
      
      // Автоматичне копіювання
      try {
        await navigator.clipboard.writeText(result.url);
        setCopied(true);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = result.url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
      }
      
      onUrlGenerated?.(result.url);
    } catch (err) {
      const message = err instanceof ApiClientError
        ? err.message
        : 'Не вдалося отримати OAuth URL. Перевірте налаштування Worker.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setOauthResult(null);
    setAccountName('');
    setError(null);
    setCopied(false);
  };

  return (
    <div className="form-panel glass-card">
      <div className="flex items-center gap-3" style={{ marginBottom: 'var(--space-4)' }}>
        <div>
          <h3 className="form-panel__title" style={{ marginBottom: 0 }}>
            Підключити акаунт
          </h3>
          <p className="text-xs text-muted" style={{ marginTop: 2 }}>
            Генерує OAuth URL для ручного входу
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="field">
            <label htmlFor={inputId} className="label">
              Ім'я акаунту
            </label>
            <input
              id={inputId}
              type="text"
              className="input"
              placeholder="Наприклад: John Doe"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              disabled={loading || !!oauthResult}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <button
            type="submit"
            className={`btn btn--primary${loading ? ' btn--loading' : ''}`}
            disabled={loading || !!oauthResult}
            style={{ height: 44, alignSelf: 'flex-end' }}
          >
            {loading ? 'Завантаження...' : 'Отримати URL'}
          </button>
        </div>
      </form>

      {/* Помилка */}
      {error && (
        <div className="notification notification--error" style={{ marginTop: 'var(--space-4)' }}>
          <span>{error}</span>
        </div>
      )}

      {/* Результат — OAuth URL */}
      {oauthResult && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <div style={{ marginBottom: 'var(--space-2)', fontWeight: 500, color: 'var(--color-success)' }}>
            {copied ? 'URL автоматично скопійовано!' : 'URL згенеровано!'}
          </div>
          <div className="flex gap-2 items-center">
            <input 
              type="text" 
              className="input" 
              value={oauthResult.url} 
              readOnly 
              style={{ flex: 1, fontSize: '0.85rem' }} 
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleReset}
            >
              Новий акаунт
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
